/**
 * AITutorPanel (Sprint 7 B5) — Drawer side panel chat với AI Tutor.
 *
 * Props:
 *   - open: bool — Drawer visibility
 *   - onClose: callback close
 *   - conversationId: number — đã tạo trước (parent caller chịu trách nhiệm)
 *   - lessonSlug: optional context (hiển thị trong header)
 *   - suggestedQuestions: optional override default 3 câu
 *
 * Layout: Drawer right 420px:
 *   - Header: tiêu đề + lesson info
 *   - MessageList (flex: 1, scrollable)
 *   - ChatInput (sticky bottom)
 */

import { Alert, Drawer, Space, Typography } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import { useMessages } from "@/api/learning";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";
import { useStreamingChat } from "./useStreamingChat";

const { Text, Title } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  conversationId: number | undefined;
  lessonTitle?: string;
  suggestedQuestions?: string[];
  width?: number | string;
}

export function AITutorPanel({
  open,
  onClose,
  conversationId,
  lessonTitle,
  suggestedQuestions,
  width = 420,
}: Props) {
  const { data: messagesPage } = useMessages(conversationId);
  const messages = messagesPage?.results ?? [];
  const { partial, citations: partialCitations, streaming, error, send } = useStreamingChat(
    conversationId,
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      title={
        <Space direction="vertical" size={0} style={{ width: "100%" }}>
          <Space>
            <RobotOutlined style={{ color: "#722ed1" }} />
            <Title level={5} style={{ margin: 0 }}>
              AI Tutor
            </Title>
          </Space>
          {lessonTitle && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {lessonTitle}
            </Text>
          )}
        </Space>
      }
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
      data-testid="ai-tutor-panel"
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message="Lỗi gửi câu hỏi"
          description={error}
          closable
          style={{ margin: 12 }}
        />
      )}
      <MessageList
        messages={messages}
        partial={partial}
        partialCitations={partialCitations}
        streaming={streaming}
        suggestedQuestions={suggestedQuestions}
        onSuggestionClick={(text) => void send(text)}
      />
      <ChatInput
        onSend={(text) => void send(text)}
        disabled={streaming || !conversationId}
        placeholder={
          streaming
            ? "Đang nhận trả lời..."
            : conversationId
              ? undefined
              : "Đang khởi tạo phiên chat..."
        }
      />
    </Drawer>
  );
}

export default AITutorPanel;
